
/* ==========================================================================
   UNIVERSAL CATALOG ID INDEX (/database/catalog-identity.js)
   Backend IDs are authority. The browser never generates or re-hashes identity.
   ========================================================================== */

let catalogById = new Map();
let ambiguousCatalogIds = new Map();
let identityMeta = null;

function stableRecordFingerprint(kind, record) {
  const entries = Object.entries(record || {})
    .filter(([key]) => !['id', 'catalogId', 'catalog_id'].includes(key))
    .sort(([a], [b]) => a.localeCompare(b));
  return `${String(kind || '').toLowerCase()}|${JSON.stringify(entries)}`;
}

export function catalogIdOf(record) {
  return String(record?.catalogId ?? record?.catalog_id ?? record?.id ?? '').trim().toLowerCase();
}

function inferHardwareKind(bucketName = '') {
  const key = String(bucketName || '').toLowerCase().replace(/[^a-z0-9]+/g, '_');
  if (key.includes('gpu')) return 'gpu';
  if (key.includes('aio') || key.includes('radiator') || key.includes('liquid')) return 'aio';
  if (key.includes('ram') || key.includes('memory') || key.includes('dimm')) return 'ram';
  if (key.includes('psu') || key.includes('power_supply') || key.includes('powersupply')) return 'psu';
  if (key.includes('motherboard') || key.includes('mobo') || key.includes('mitx') || key.includes('matx') || key.includes('itx_board') || key.includes('board')) return 'motherboard';
  if (key.includes('cooler')) return 'aircooler';
  if (key.includes('cpu')) return 'cpu';
  return 'hardware';
}

function register(kind, record) {
  if (!record || typeof record !== 'object') return;
  const id = catalogIdOf(record);
  if (!id || ambiguousCatalogIds.has(id)) return;

  const next = { kind, record, fingerprint: stableRecordFingerprint(kind, record) };
  const existing = catalogById.get(id);
  if (!existing) {
    catalogById.set(id, next);
    return;
  }

  // Identical duplicate rows are harmless. Distinct records sharing one backend ID are not:
  // never let "first record wins" silently load the wrong component.
  if (existing.fingerprint === next.fingerprint) return;

  catalogById.delete(id);
  ambiguousCatalogIds.set(id, { id, kinds: [...new Set([existing.kind, kind])] });
}

export function rebuildCatalogIdentityIndex(cases = [], hardware = {}, meta = null) {
  catalogById = new Map();
  ambiguousCatalogIds = new Map();
  identityMeta = meta && typeof meta === 'object' ? { ...meta } : null;

  (Array.isArray(cases) ? cases : []).forEach(record => register('case', record));

  const source = hardware?.hardware && typeof hardware.hardware === 'object' ? hardware.hardware : hardware;
  if (source && typeof source === 'object') {
    for (const [bucket, records] of Object.entries(source)) {
      if (!Array.isArray(records)) continue;
      const kind = inferHardwareKind(bucket);
      records.forEach(record => register(kind, record));
    }
  }

  if (typeof window !== 'undefined') {
    window.resolveSffCatalogId = (id, expectedKind = null) => resolveCatalogId(id, expectedKind);
    window.getSffCatalogIdentityStats = () => ({
      schema: identityMeta?.schema ?? identityMeta?.identity_schema ?? 1,
      records: catalogById.size,
      ambiguous_ids: ambiguousCatalogIds.size,
      ambiguous: [...ambiguousCatalogIds.keys()],
      backend: identityMeta
    });
  }
  return catalogById;
}

export function resolveCatalogId(id, expectedKind = null) {
  const key = String(id || '').trim().toLowerCase();
  if (ambiguousCatalogIds.has(key)) return null;
  const hit = catalogById.get(key) || null;
  if (!hit || !expectedKind) return hit;

  const expected = String(expectedKind).toLowerCase();
  const actual = String(hit.kind || '').toLowerCase();
  const equivalent =
    (expected === 'radiator' && actual === 'aio') ||
    (expected === 'aio' && actual === 'radiator') ||
    (expected === 'aircooler' && (actual === 'cpu_cooler' || actual === 'cooler')) ||
    (expected === 'motherboard' && actual === 'mobo');
  return actual === expected || equivalent ? hit : null;
}

export function getCatalogIdentityMeta() {
  return identityMeta ? { ...identityMeta } : null;
}
